import type { PopoverDismissGuard } from '@frontendlib/components/features/popover/types/config';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, viewChild } from '@angular/core';
import { ContextMenuDividerComponent } from '@frontendlib/components/features/popover/components/context-menu-divider/context-menu-divider.component';
import { ContextMenuItemComponent } from '@frontendlib/components/features/popover/components/context-menu-item/context-menu-item.component';
import { CLOSE_POPOVER } from '@frontendlib/components/features/popover/types/close-popover.injection-token';
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import type { FormFolderSummaryDto, FormSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import { from, tap, type Observable } from 'rxjs';
import { PanelFormCreateComponent } from '../components/creation/panel-form-create/panel-form-create.component';
import { FormsQuickFormComponent } from '../components/creation/forms-quick-form/forms-quick-form.component';
import { FormsWorkspaceStore } from '../services/forms-workspace.store';
import { FormsSidebarActionsService, type FormsCollectionOperation } from '../services/forms-sidebar-actions.service';
import type { NativeForm } from '../services/forms-api.service';

/** Mechanical Forms adaptation of BoardsSidebarContextMenu: the same native entries, quick form and collection actions. */
@Component({
  selector: 'app-forms-sidebar-context-menu',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ContextMenuItemComponent, ContextMenuDividerComponent, FormsQuickFormComponent, PanelFormCreateComponent],
  templateUrl: './forms-sidebar-context-menu.component.html',
})
export class FormsSidebarContextMenuComponent {
  public readonly view = input.required<string>();
  public readonly formCreated = output<FormSummaryDto>();
  public readonly folderCreated = output<FormFolderSummaryDto>();
  protected readonly actions = inject(FormsSidebarActionsService);
  private readonly creationControl = viewChild(PanelFormCreateComponent);
  private readonly store = inject(FormsWorkspaceStore);
  private readonly closePopover = inject(CLOSE_POPOVER, { optional: true });
  private readonly followUpWording = injectFollowUpWording();
  protected readonly addsForm = computed(() =>
    ['all', 'recent', 'flagged', 'followup', 'snoozed'].includes(this.view()),
  );
  protected readonly addsFolder = computed(() => ['flagged', 'followup', 'snoozed', 'folders'].includes(this.view()));
  protected readonly action = computed<{ title: string; operation: FormsCollectionOperation } | null>(() => {
    switch (this.view()) {
      case 'flagged':
        return { title: 'Unflag all', operation: 'unflag-all' };
      case 'followup':
        return { title: this.followUpWording.removeAllFollowUps(), operation: 'remove-all-follow-ups' };
      case 'snoozed':
        return { title: 'Unsnooze all', operation: 'unsnooze-all' };
      case 'archived':
        return { title: 'Delete all', operation: 'trash-all-archived' };
      case 'deleted':
        return { title: 'Empty trash', operation: 'empty-trash' };
      default:
        return null;
    }
  });
  protected readonly creationDismissGuard: PopoverDismissGuard = { canDismiss: () => this.creationControl()?.canLeaveHost() ?? true, setOpen: () => undefined };
  protected readonly beforeCreate = async (initiator?: PanelFormCreateComponent): Promise<boolean> =>
    await this.store.beforeLeave(false, initiator?.activePreflightGuardKey);
  protected async openCreated(item: NativeForm): Promise<void> {
    if (item.accountId !== this.store.space.contextId()) return;
    await this.actions.applyRowState(item, this.view()).catch((error) => this.store.fail(error));
    this.store.ensureVisibleAfterCreate(this.view());
    this.formCreated.emit(item);
    this.close();
  }
  protected readonly fileFolder = (title: string): Observable<unknown> =>
    from(this.actions.create('folder', title, this.view())).pipe(
      tap((item: FormFolderSummaryDto) => this.folderCreated.emit(item)),
    );
  protected close(): void {
    this.closePopover?.next();
  }
}
